import { LuTriangleAlert } from "react-icons/lu";
import SetupStepper from "../../../common/SetupStepper";
import TopBar from "../../../common/TopBar";
import ActionBar from "../../../common/ActionBar";
import PlanManagerModal from "../../../common/PlanManagerModal";
import PlanDesign from "./children/PlanDesign";
import VendorDetails from "./children/VendorDetails";
import MemberCosts from "./children/MemberCosts";
import Accumulators from "./children/Accumulators";
import SetupStep from "./children/SetupStep";
import {
    useSetup,
    SUBMIT_MODAL_COPY,
    PLAN_SETUP_REVIEW_BANNER,
} from "./useSetup";
import { PLANS_LIST_ROUTE } from "../../../common/constants";
import "./Setup.scss";
import { PlanSetupPlatform } from "../../../../../utils/planManager/planConfigurationUtils.js";

/**
 * Plan setup wizard — presentational shell delegating state to {@link useSetup}.
 *
 * @returns {import('react').JSX.Element}
 */
const PlansSetup = () => {
    const {
        form,
        activeStep,
        isHrp,
        isReview,
        visibleSteps,
        currentStep,
        nextLabel,
        isConfirmModalOpen,
        stepErrors,
        validationAlert,
        options,
        planId,
        isLoading,
        isError,
        isSubmitting,
        uploadingDocType,
        loadErrorMessage,
        memberCostsFieldErrors,
        handleFieldChange,
        handleFieldBlur,
        handleIncludeUnguidedColumnChange,
        handleUploadDocument,
        handleRemoveDocument,
        handleRowChange,
        handleRowToggle,
        handleToggleAllRows,
        handleAddRow,
        handleResetAccumulators,
        handleStepJump,
        handlePrevious,
        handleNext,
        handleConfirmSubmit,
        handleCloseConfirmModal,
    } = useSetup();

    const renderActiveStep = () => {
        switch (currentStep?.id) {
            case "planDesign":
                return (
                    <PlanDesign
                        values={form.planDesign}
                        errors={stepErrors}
                        onFieldChange={handleFieldChange("planDesign")}
                        onFieldBlur={handleFieldBlur("planDesign")}
                    />
                );
            case "vendorDetails":
                return (
                    <VendorDetails
                        values={form.vendorDetails}
                        options={options}
                        errors={stepErrors}
                        onFieldChange={handleFieldChange("vendorDetails")}
                    />
                );
            case "memberCosts":
                return (
                    <MemberCosts
                        isHrp={isHrp}
                        values={form.memberCosts}
                        unguidedTierLabelError={
                            memberCostsFieldErrors.unguidedTierLabelError
                        }
                        errors={stepErrors}
                        onFieldChange={handleFieldChange("memberCosts")}
                        onIncludeUnguidedColumnChange={
                            handleIncludeUnguidedColumnChange
                        }
                        onRowChange={handleRowChange}
                        onRowToggle={handleRowToggle}
                        onToggleAll={handleToggleAllRows}
                        onAddRow={handleAddRow}
                    />
                );
            case "accumulators":
                return (
                    <Accumulators
                        values={form.accumulators}
                        errors={stepErrors}
                        onFieldChange={handleFieldChange("accumulators")}
                        onReset={handleResetAccumulators}
                    />
                );
            case "setup":
                return (
                    <SetupStep
                        values={form.setup}
                        errors={stepErrors}
                        onFieldChange={handleFieldChange("setup")}
                        onUploadDocument={handleUploadDocument}
                        onRemoveDocument={handleRemoveDocument}
                        uploadingDocType={uploadingDocType}
                    />
                );
            default:
                return null;
        }
    };

    const actionAlerts = validationAlert
        ? [{ tone: "error", message: validationAlert }]
        : [];

    return (
        <div className="plan-setup-page">
            <TopBar variant="detail">
                <TopBar.Back
                    to={PLANS_LIST_ROUTE}
                    label="Plans"
                    ariaLabel="Back to plans"
                />
                <TopBar.Title>{planId ?? ""}</TopBar.Title>
                <TopBar.Actions>
                    <span className="plan-manager-top-bar__platform-value">
                        {isHrp
                            ? PlanSetupPlatform.HRP
                            : PlanSetupPlatform.JAVELINA}
                    </span>
                </TopBar.Actions>
            </TopBar>

            {isLoading && (
                <p className="plan-setup-page__loading" role="status">
                    Loading plan setup...
                </p>
            )}

            {isError && (
                <p className="plan-setup-page__error" role="alert">
                    {loadErrorMessage}
                </p>
            )}

            <div className="plan-setup-page__stepper">
                <SetupStepper
                    steps={visibleSteps}
                    activeStep={activeStep}
                    onClickHandler={handleStepJump}
                />
            </div>

            <div className="plan-setup-page__content">
                {isReview && !isLoading && !isError && (
                    <div
                        className="plan-setup-page__review-banner"
                        role="status"
                    >
                        <div className="plan-setup-page__review-banner-header">
                            <LuTriangleAlert aria-hidden size={18} />
                            <h4>{PLAN_SETUP_REVIEW_BANNER.TITLE}</h4>
                        </div>
                        <p>{PLAN_SETUP_REVIEW_BANNER.BODY}</p>
                    </div>
                )}
                {!isLoading && !isError && renderActiveStep()}
            </div>

            <ActionBar
                layout="spread"
                alerts={actionAlerts}
                actions={[
                    {
                        label: "Previous",
                        variant: "secondary",
                        disabled: activeStep <= 1,
                        onClick: handlePrevious,
                    },
                    {
                        label: nextLabel,
                        variant: "primary",
                        disabled: isSubmitting || Boolean(uploadingDocType),
                        onClick: handleNext,
                    },
                ]}
            />

            <PlanManagerModal
                isOpen={isConfirmModalOpen}
                onClose={handleCloseConfirmModal}
                title={SUBMIT_MODAL_COPY.TITLE}
                body={SUBMIT_MODAL_COPY.BODY}
                primaryText={SUBMIT_MODAL_COPY.CONFIRM}
                secondaryText={SUBMIT_MODAL_COPY.CANCEL}
                onPrimary={handleConfirmSubmit}
                onSecondary={handleCloseConfirmModal}
            />
        </div>
    );
};

export default PlansSetup;
